/* jp-recent-products.js — consent-gated recently viewed product rail.
   Stores handles only, never product copy or browsing analytics. A missing or
   denied preference consent leaves the rail hidden; product JSON is fetched
   only for handles the shopper has explicitly visited while consent was ready. */
(() => {
  if (window.__jpRecentProductsInit) return;
  window.__jpRecentProductsInit = true;

  const KEY = 'jp_recent_products';
  const MAX_STORED = 8;
  const MAX_RENDERED = 4;
  const privacy = () => window.JapanScissors?.privacy;
  const esc = (value) => String(value == null ? '' : value).replace(/[<>&"']/g, (char) => ({
    '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;',
  }[char]));
  const rootPath = (root) => {
    const path = root?.dataset.productsRoot || window.Shopify?.routes?.root || '/';
    return path.endsWith('/') ? path : `${path}/`;
  };
  const read = () => {
    try {
      const value = privacy()?.getPreference(KEY);
      const handles = JSON.parse(value || '[]');
      return Array.isArray(handles)
        ? handles.filter((handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/i.test(handle)).slice(0, MAX_STORED)
        : [];
    } catch { return []; }
  };
  const write = (handles) => privacy()?.setPreference(KEY, JSON.stringify(handles.slice(0, MAX_STORED)));
  const preferenceReady = () => {
    const store = privacy();
    return Boolean(store?.getState?.().ready && store.preferencesAllowed?.());
  };
  const saveCurrent = (root) => {
    const handle = String(root.dataset.currentHandle || '').trim();
    if (!handle || !preferenceReady()) return;
    const recent = read().filter((item) => item !== handle);
    recent.unshift(handle);
    write(recent);
  };
  const formatMoney = (root, cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: root.dataset.currency || 'GBP',
        minimumFractionDigits: Number(cents) % 100 ? 2 : 0,
      }).format(Number(cents) / 100);
    } catch { return (Number(cents) / 100).toFixed(2); }
  };
  const productUrl = (root, handle) => `${rootPath(root)}products/${encodeURIComponent(handle)}`;
  const fetchProduct = async (root, handle) => {
    const response = await fetch(`${productUrl(root, handle)}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('product');
    return response.json();
  };
  const productCard = (root, product) => {
    const image = product.featured_image || product.images?.[0] || '';
    const price = Number(product.price_min ?? product.price ?? 0);
    const pricePrefix = product.price_varies ? `<span class="jp-rv__from">${esc(root.dataset.from || 'From')}</span> ` : '';
    const imageMarkup = image
      ? `<img src="${esc(image)}" alt="" loading="lazy" width="360" height="360">`
      : '<span class="jp-rv__placeholder" aria-hidden="true"></span>';
    return `<article class="jp-rv__item" role="listitem"><a class="jp-rv__card" href="${esc(productUrl(root, product.handle))}">`
      + `<span class="jp-rv__media">${imageMarkup}</span>`
      + '<span class="jp-rv__body">'
      + `<span class="jp-rv__title">${esc(product.title)}</span>`
      + `<span class="jp-rv__price">${pricePrefix}${esc(formatMoney(root, price))}</span>`
      + `<span class="jp-rv__view">${esc(root.dataset.view || 'View product')} <span aria-hidden="true">→</span></span>`
      + '</span></a></article>';
  };
  const render = async (root) => {
    const list = root.querySelector('[data-jp-recent-products-list]');
    if (!list) return;
    const renderToken = (root.__jpRecentRenderToken || 0) + 1;
    root.__jpRecentRenderToken = renderToken;
    root.hidden = true;
    list.innerHTML = '';
    root.removeAttribute('aria-busy');
    if (!preferenceReady()) return;
    const currentHandle = String(root.dataset.currentHandle || '').trim();
    const currentProductId = String(root.dataset.currentProductId || '').trim();
    const limit = Math.min(MAX_RENDERED, Math.max(1, Number(root.dataset.limit || MAX_RENDERED)));
    const handles = read().filter((handle) => handle !== currentHandle).slice(0, limit);
    if (!handles.length) return;
    root.setAttribute('aria-busy', 'true');
    const results = await Promise.allSettled(handles.map((handle) => fetchProduct(root, handle)));
    /* Consent can be revoked while product JSON is in flight. A newer render
       owns the DOM, and this completion must never reveal stale private state. */
    if (root.__jpRecentRenderToken !== renderToken || !preferenceReady()) return;
    const seen = new Set();
    const products = results.filter((result) => result.status === 'fulfilled').map((result) => result.value).filter((product) => {
      if (!product?.available || !product?.handle) return false;
      if (String(product.handle) === currentHandle) return false;
      if (currentProductId && String(product.id || '') === currentProductId) return false;
      const key = String(product.id || product.handle);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (products.length) {
      list.innerHTML = products.map((product) => productCard(root, product)).join('');
      root.hidden = false;
    }
    if (root.__jpRecentRenderToken === renderToken) root.removeAttribute('aria-busy');
  };
  const init = (root) => {
    if (!root || root.__jpRecentProductsBound) return;
    root.__jpRecentProductsBound = true;
    saveCurrent(root);
    render(root).catch(() => { root.hidden = true; root.removeAttribute('aria-busy'); });
  };
  const boot = () => document.querySelectorAll('[data-jp-recent-products]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('jp:privacy:change', () => {
    document.querySelectorAll('[data-jp-recent-products]').forEach((root) => {
      saveCurrent(root);
      render(root).catch(() => { root.hidden = true; root.removeAttribute('aria-busy'); });
    });
  });
})();
